import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ChatMessage } from '../types/Message.types';

interface ChatState {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  setMessages: (messages: ChatMessage[]) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

// Create a store factory
const createChatStore = (userId: string) => create<ChatState>()(
  persist(
    (set) => ({
      messages: [],
      isLoading: false,
      error: null,
      setMessages: (messages) => set({ messages }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
    }),
    {
      name: `chat-storage-${userId}`,
      partialize: (state) => ({ messages: state.messages }),
      storage: {
        getItem: (name) => {
          const stored = localStorage.getItem(name);
          if (!stored) return null;

          const data = JSON.parse(stored);
          const now = Date.now();
          const age = now - (data.timestamp || 0);

          // Reset if older than 24 hours
          if (age > 24 * 60 * 60 * 1000) {
            localStorage.removeItem(name);
            return null;
          }

          return { state: data.state, version: data.version };
        },
        setItem: (name, value) => {
          const data = { ...value, timestamp: Date.now() };
          localStorage.setItem(name, JSON.stringify(data));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);

// Create a map to store instances
const storeMap = new Map<string, ReturnType<typeof createChatStore>>();

// Export a function to get or create a store for a user
export const getChatStore = (userId: string) => {
  if (!storeMap.has(userId)) {
    storeMap.set(userId, createChatStore(userId));
  }
  return storeMap.get(userId)!;
};
